import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';

import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FaConfig, FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { fal } from '@fortawesome/pro-light-svg-icons';
import { fas } from '@fortawesome/pro-solid-svg-icons';

import { HeaderComponent } from './shared/components/header/header.component';
import { SidebarComponent } from './shared/components/sidebar/sidebar.component';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import {
  MomentDateAdapter,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
} from '@angular/material-moment-adapter';
import { MatDatepickerModule } from '@angular/material/datepicker';
import 'moment/locale/es';
import { User } from './core/models/user.models';
import { ComponentPortal, Portal, PortalModule } from '@angular/cdk/portal';
import { HelpDialogComponent } from './shared/components/help-dialog/help-dialog.component';
import { Platform } from '@angular/cdk/platform';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { HelpDialogModel, HelpService } from './core/services/help/help.service';
import { CookiesDialogComponent } from './shared/components/cookies-dialog/cookies-dialog.component';
import { FadeIn } from './core/animations';
import { LoaderService } from './core/services/loader/loader.service';
import { MediaQueryModule } from './shared/directives/media-querys/media-query.module';
import { PlatformServices } from './core/services/platform/platform.service';
import { LoginService } from './core/services/login/login.service';
import { StatisticsConfigurationsDialogComponent } from './shared/components/statistics-configurations-dialog/statistics-configurations-dialog.component';
import { SidebarService } from './core/services/sidebar/sidebar.service';


@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    TranslateModule,
    FontAwesomeModule,
    HeaderComponent,
    SidebarComponent,
    MatDatepickerModule,
    PortalModule,
    MatDialogModule,
    FontAwesomeModule,
    MediaQueryModule
  ],
    providers: [
    {provide: MAT_DATE_LOCALE, useValue: 'es-ES'},
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FORMATS},
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: ['l', 'LL'],
        },
        display: {
          dateInput: 'L',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      }
    },

    ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  animations: [FadeIn(500, false)]

})
export class AppComponent {

  private routesPublic = ['login', 'changepassword','forgot-password']; // List of routes that do not have a sidebar & navbar
  private routesMobilePublic = ['fee', 'help', 'configuration']  // List of routes that do not have a mobile footer

  public hideSidebarNavbar = true;
  public hideMobileFooter: boolean = false;
  public showFooterFloatButton: boolean = false;
  public changeSectionHeight: boolean = false;

  componentPortal!: ComponentPortal<HelpDialogComponent | CookiesDialogComponent 
  | StatisticsConfigurationsDialogComponent>;

  selectedPortal!: Portal<any | null>;

  private helpDialogRef!: MatDialogRef<HelpDialogComponent | CookiesDialogComponent 
  | StatisticsConfigurationsDialogComponent>;

  isSidebarOpen: boolean = false;

  isInited = false;

  showLoader = false;

  currentScreen: 'Tablet' | 'Handset' | 'Web' |  string = '';

  isLoged: User | null = null;
  isDoctor: boolean = false;

  constructor(
    private faConfig: FaConfig,
    private translate: TranslateService,
    private library: FaIconLibrary,
    private router: Router,
    private dialog: MatDialog,
    private helpDialogService: HelpService,
    private loaderService: LoaderService,
    private platformServices: PlatformServices,
    private loginService: LoginService,
    private sidebarService: SidebarService
  ) {
    this.library.addIconPacks(fal);
    this.library.addIconPacks(fas);
    this.faConfig.fixedWidth = true;
    this.listenRoutesChanges();
    if( sessionStorage.getItem('user') ){
      const user: User = JSON.parse( sessionStorage.getItem('user') ?? '' );
      if( user ){
        this.changeLang( user.language );
        this.isDoctor = !!user.externalId;
      }
    }
    this.platformServices.screenChange.subscribe( v => {
      this.currentScreen = v;
    } )
  }

  API_URL = environment.URL_API;
  ENVIROMENT_LABEL = environment.ENVIROMENT_LABEL;
  PRODUCTION = environment.PRODUCTION;
  VERSION = environment.VERSION

  ngAfterViewInit() {
    this.helpDialogService.helpController.subscribe( (value: HelpDialogModel) =>{
      if( value.show && value.type === 'help' ){
        this.openHelp();
        return;
      }
      if( !value.show && value.type === 'help' ){
        this.closeHelp();
        return;
      }

      if( value.show && value.type === 'cookies' ){
        this.openCookies();
        return;
      }
      if( !value.show && value.type === 'cookies' ){
        this.closeHelp();
        return 
      }

      if( value.show && value.type === 'statistics_configurations' ){
        this.openMobileStatisticsConfigurations();
        return;
      }

    });

    window.addEventListener('resize', this.appHeight)
    this.appHeight();
    const width  = window.innerWidth;  
    if( width >=1400 ){
      this.isSidebarOpen = true;

    }

    this.loaderService.loader.subscribe( (show: boolean)=> this.showLoader = show )
  
  }
  
   appHeight = () => {
    const doc = document.documentElement
    doc.style.setProperty('--app-height', `${window.innerHeight}px`)
  }

  changeLang(lang: string) {
    this.translate.setDefaultLang(lang);
    this.translate.use(lang);
  }

  private listenRoutesChanges(){
    this.router.events.subscribe((val) => {
      if( val instanceof NavigationEnd ) {
        this.hideSidebarNavbar = val.urlAfterRedirects === '/' || this.routesPublic.some( r => val.urlAfterRedirects.includes(r));
        const width  = window.innerWidth;  

        if( this.hideSidebarNavbar ){
           this.isSidebarOpen = false;
           if( width >=1400 ){
            this.isSidebarOpen = true;

          }
        }

        if(this.currentScreen === "Web") {
          if(val.urlAfterRedirects === '/home') {
            this.changeSectionHeight = true;
          } else {
            this.changeSectionHeight = false;
          }
        }

        if(width <= 960) {
          this.hideMobileFooter = val.urlAfterRedirects === '/' || this.routesMobilePublic.some( r => val.urlAfterRedirects.includes(r));
          this.showFooterFloatButton = val.urlAfterRedirects === '/home';

          // if(!this.isDoctor) {
          //   this.hideMobileFooter = true;
          // }
        }

        this.closeHelp();
      }
    });
  }

  openHelp(){
    if(this.currentScreen === 'Tablet' || this.currentScreen === 'Handset'){
      this.componentPortal = new ComponentPortal(HelpDialogComponent);
      this.selectedPortal = this.componentPortal;
      this.router.navigate([], {
        queryParams: {
          current: 'help'
        },
        queryParamsHandling: 'merge',
      });
    }else{
      this.helpDialogRef = this.dialog.open( HelpDialogComponent,
        {width: '650px',
        panelClass: 'dialog-panel-class'
      });
      
    }
  }

  openCookies(){
    if(this.currentScreen === 'Tablet' || this.currentScreen === 'Handset'){
      this.componentPortal = new ComponentPortal(CookiesDialogComponent);
      this.selectedPortal = this.componentPortal;
    }else{
      this.helpDialogRef = this.dialog.open( CookiesDialogComponent,
        {width: '650px',
        panelClass: 'dialog-panel-class',
        autoFocus: false
      });
      
    }
  }
  
  openMobileStatisticsConfigurations() {
    this.hideMobileFooter = true;

    const dialogRef = this.dialog.open( StatisticsConfigurationsDialogComponent, {
      height: "calc(100% - 85px)",
      width: "100%",
      maxWidth: "100%",
      position: { top: '85px' },
      
    })

    dialogRef.afterClosed().subscribe((response: any) => this.hideMobileFooter = false)


  }

  closeCookies(){
    if(this.currentScreen === 'Tablet' || this.currentScreen === 'Handset'){
      if( this.selectedPortal?.isAttached ){
        this.selectedPortal.detach();
      }
    }else{
      if( this.helpDialogRef && this.helpDialogRef?.getState() === 0){
        this.helpDialogRef.close();
      }

    }
  }

  closeHelp(){
    if(this.currentScreen === 'Tablet' || this.currentScreen === 'Handset'){
      if( this.selectedPortal?.isAttached ){
        this.selectedPortal.detach();
      }
    }else{
      if( this.helpDialogRef && this.helpDialogRef?.getState() === 0){
        this.helpDialogRef.close();
      }

    }
  }
  

  goToLink(url: string) {
    window.open(url, "_blank");
  }

  openSidebar(open: boolean){
    this.isSidebarOpen = open;
    this.sidebarService.setSidebarOpen(open);
    // this.isInited = true;
  }

  loadUser() {
    this.loginService.getUser().subscribe(
      {
        next: (user) => {
          this.isLoged = user;
        }
      }
    )
  }
  
  ngOnInit() {
    this.loadUser();
  }
}
